import React from "react";
import { Box, Button, Container, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { palette } from "../palette";
import { fadeUp, revealProps, stagger, useReducedMotion } from "../motion";
import { GrainDefs, GrainOverlay } from "../illustrations/Grain";

const ID = "cta";
const c = palette.ill;

export const FinalCTA = ({ onRegister }) => {
  const reduced = useReducedMotion();

  return (
    <Box component="section" aria-labelledby="cta-heading" sx={{ py: { xs: 8, md: 12 } }}>
      <Container maxWidth="lg">
        <motion.div variants={stagger(0.09)} {...revealProps(reduced)}>
          <Box
            sx={{
              position: "relative",
              overflow: "hidden",
              borderRadius: "26px",
              px: { xs: 3.5, md: 8 },
              py: { xs: 6, md: 9 },
              textAlign: "center",
              backgroundColor: c.sky,
            }}
          >
            {/* Grain and shapes sit behind the copy */}
            <Box
              component="svg"
              aria-hidden="true"
              viewBox="0 0 1200 420"
              preserveAspectRatio="xMidYMid slice"
              sx={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                pointerEvents: "none",
              }}
            >
              <defs>
                <GrainDefs id={ID} />
              </defs>
              {/* Fully contained shapes — a circle cropped by the panel edge
                  reads as a mistake rather than a flourish. */}
              <circle cx="96" cy="96" r="34" fill={c.sun} opacity="0.55" />
              <circle cx="168" cy="58" r="14" fill={c.sun} opacity="0.4" />
              <circle cx="1104" cy="330" r="42" fill={c.leaf} opacity="0.4" />
              <circle cx="1032" cy="378" r="18" fill={c.leaf} opacity="0.3" />
              <circle cx="1140" cy="74" r="20" fill={c.plum} opacity="0.22" />
              {[0, 1, 2].map((i) => (
                <motion.path
                  key={i}
                  d={`M-60 ${150 + i * 55} Q 300 ${110 + i * 55} 620 ${150 + i * 55} T 1300 ${150 + i * 55}`}
                  fill="none"
                  stroke={c.river}
                  strokeWidth="3"
                  strokeLinecap="round"
                  opacity={0.18}
                  strokeDasharray="16 26"
                  {...(reduced
                    ? {}
                    : {
                        animate: { strokeDashoffset: [0, -84] },
                        transition: {
                          duration: 11 + i * 2,
                          repeat: Infinity,
                          ease: "linear",
                        },
                      })}
                />
              ))}
              <GrainOverlay id={ID} width={1200} height={420} opacity={0.3} />
            </Box>

            <Box sx={{ position: "relative" }}>
              <motion.div variants={fadeUp}>
                <Typography
                  component="h2"
                  id="cta-heading"
                  sx={{
                    color: palette.text,
                    fontWeight: 700,
                    letterSpacing: "-0.03em",
                    lineHeight: 1.1,
                    fontSize: { xs: 30, sm: 38, md: 46 },
                  }}
                >
                  Start with a conversation
                </Typography>
              </motion.div>
              <motion.div variants={fadeUp}>
                <Typography
                  component="p"
                  sx={{
                    color: palette.textMuted,
                    fontSize: { xs: 16, md: 18 },
                    lineHeight: 1.6,
                    maxWidth: 560,
                    mx: "auto",
                    mt: 2.5,
                  }}
                >
                  Two fields, no card, no obligation. Tell us where you are
                  and what you're aiming at, and we'll tell you honestly
                  whether we're the right people for it — and roughly how long
                  it would take.
                </Typography>
              </motion.div>
              <motion.div variants={fadeUp}>
                <Button
                  onClick={onRegister}
                  disableElevation
                  sx={{
                    mt: 4,
                    px: 4,
                    py: 1.6,
                    borderRadius: "999px",
                    background: palette.ctaGradient,
                    color: "#ffffff",
                    fontSize: 16.5,
                    fontWeight: 600,
                    textTransform: "none",
                    "&:hover": { background: palette.primaryHover },
                  }}
                >
                  Talk to us
                </Button>
              </motion.div>
            </Box>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default FinalCTA;
